import React, { useState } from 'react';
import { Organization, orgManagementService } from '../services/orgManagementService';
import { BRAND_ICONS, toIconValue, iconNameFromValue } from '../services/brandIcons';

interface Props {
  org: Organization;
  onClose: () => void;
  onSaved: () => void;
}

const BrandingSettingsModal: React.FC<Props> = ({ org, onClose, onSaved }) => {
  const [brandName, setBrandName] = useState(org.brandName ?? '');
  const [brandSubtitle, setBrandSubtitle] = useState(org.brandSubtitle ?? '');
  // 选中的预置图标名（null = 未选，沿用默认 psychology）。从已存的 icon: 值还原。
  const [selectedIcon, setSelectedIcon] = useState<string | null>(iconNameFromValue(org.brandLogoUrl));
  // null = 未设置主色（保存时不写入，避免把未设主色的机构默认染成 teal）。
  const [brandColor, setBrandColor] = useState<string | null>(org.brandColor ?? null);
  const COLOR_FALLBACK = '#0d9488'; // 仅用于颜色选择器的占位显示，不入库
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await orgManagementService.updateBranding(org.id, {
        brandName: brandName.trim() || null,
        brandSubtitle: brandSubtitle.trim() || null,
        // 选中图标存为 icon:<name>;未选则 null（沿用默认图标）。
        brandLogoUrl: selectedIcon ? toIconValue(selectedIcon) : null,
        brandColor: brandColor,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-[480px] max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">品牌设置 · {org.name}</h2>

        <label className="block text-sm mb-1" htmlFor="brand-name">品牌名</label>
        <input id="brand-name" className="w-full border rounded-lg px-3 py-2 mb-3" value={brandName}
               onChange={(e) => setBrandName(e.target.value)} placeholder="问卷测评系统" />

        <label className="block text-sm mb-1" htmlFor="brand-subtitle">副标题</label>
        <input id="brand-subtitle" className="w-full border rounded-lg px-3 py-2 mb-3" value={brandSubtitle}
               onChange={(e) => setBrandSubtitle(e.target.value)} placeholder="Questionnaire Assessment Platform" />

        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm">图标</label>
          {selectedIcon && (
            <button type="button" className="text-xs text-red-600" onClick={() => setSelectedIcon(null)}>
              恢复默认
            </button>
          )}
        </div>
        <div className="grid grid-cols-6 gap-2 mb-4" role="group" aria-label="品牌图标选择">
          {BRAND_ICONS.map((name) => {
            const active = selectedIcon === name;
            return (
              <button
                key={name}
                type="button"
                aria-label={name}
                aria-pressed={active}
                onClick={() => setSelectedIcon(name)}
                className={`aspect-square rounded-xl flex items-center justify-center border transition-colors ${
                  active
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>{name}</span>
              </button>
            );
          })}
        </div>

        <label className="block text-sm mb-1" htmlFor="brand-color">主色</label>
        <div className="flex items-center space-x-2 mb-4">
          <input id="brand-color" type="color" value={brandColor ?? COLOR_FALLBACK}
                 onChange={(e) => setBrandColor(e.target.value)} />
          <input className="border rounded-lg px-2 py-1 w-28 text-sm" value={brandColor ?? ''}
                 onChange={(e) => setBrandColor(e.target.value || null)} placeholder="未设置" />
          {brandColor
            ? <span className="w-6 h-6 rounded" style={{ backgroundColor: brandColor }} />
            : <span className="text-xs text-gray-400">未设置</span>}
          {brandColor && (
            <button type="button" className="text-sm text-red-600" onClick={() => setBrandColor(null)}>清除</button>
          )}
        </div>

        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

        <div className="flex justify-end space-x-2">
          <button className="px-4 py-2 text-sm rounded-lg border" onClick={onClose}>取消</button>
          <button className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white disabled:opacity-50"
                  onClick={handleSave} disabled={saving}>保存</button>
        </div>
      </div>
    </div>
  );
};

export default BrandingSettingsModal;
